import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { supabase, type ContactSubmission } from "@/lib/supabase"
import * as XLSX from "xlsx"

export function AdminDashboard() {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) {
        navigate("/collection/admin")
      }
    }

    checkAuth()
  }, [navigate])

  useEffect(() => {
    const fetchSubmissions = async () => {
      const { data, error } = await supabase
        .from("contacts")
        .select("*")
        .order("created_at", { ascending: false })

      if (!error && data) {
        setSubmissions(data)
      }
      setLoading(false)
    }

    fetchSubmissions()
  }, [])

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate("/collection/admin")
  }

  const exportToCSV = () => {
    const headers = ["Date", "Name", "Email", "Phone", "Message"]
    const rows = submissions.map((s) => [
      new Date(s.created_at).toLocaleDateString(),
      s.name,
      s.email,
      s.phone || "",
      s.message,
    ])

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n")

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = `contacts_${new Date().toISOString().split("T")[0]}.csv`
    link.click()
  }

  const exportToExcel = () => {
    const data = submissions.map((s) => ({
      Date: new Date(s.created_at).toLocaleDateString(),
      Name: s.name,
      Email: s.email,
      Phone: s.phone || "",
      Message: s.message,
    }))

    const worksheet = XLSX.utils.json_to_sheet(data)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Contacts")
    XLSX.writeFile(
      workbook,
      `contacts_${new Date().toISOString().split("T")[0]}.xlsx`
    )
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy">
        <div className="text-white">Loading...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-navy px-6 py-4">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <h1 className="font-heading text-2xl text-white">Submissions</h1>
          <button
            onClick={handleLogout}
            className="rounded bg-white/10 px-4 py-2 text-sm text-white transition hover:bg-white/20"
          >
            Logout
          </button>
        </div>
      </nav>

      <div className="mx-auto max-w-7xl px-6 py-8">
        {submissions.length > 0 && (
          <div className="mb-4 flex gap-3">
            <button
              onClick={exportToCSV}
              className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-green-700"
            >
              Export to CSV
            </button>
            <button
              onClick={exportToExcel}
              className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-green-700"
            >
              Export to Excel
            </button>
          </div>
        )}
        {submissions.length === 0 ? (
          <div className="rounded-lg bg-white p-8 text-center shadow">
            <p className="text-gray-500">No submissions yet</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg bg-white shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Message
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {submissions.map((submission) => (
                  <tr key={submission.id}>
                    <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {new Date(submission.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-gray-900">
                      {submission.name}
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {submission.email}
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {submission.phone || "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {submission.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
